import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { getDecisionCriteria, rankApps } from '../../../api/decisions.api';
import { getFieldName, transformCriteriaData } from '../../../utils';
import { AppCriteria } from '../../../types/decisions';
import { initialAppCriteria } from '../../../shared/consts';
import { useError } from '../../../context/error-context';
import AppCriteriaFields from '../../application/app-components/AppCriteriaFields';

const GET_DECISION_IN_FORM_APP_ID = 1419;

const pageInfo = {
    current_page: 1,
    total_pages: 1,
    total: 1,
    page_size: 1,
    end: 1,
    start: 1,
};

const getDecisionInFormSx = {
    width: '100%',
    px: 2,
    py: 1.25,
    border: 'none',
    backgroundColor: '#d1ecf1',
    borderRadius: '4px',
    color: '#000',
    fontSize: '22px',
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
    lineHeight: '1.4375em',
    padding: '16px',
    boxSizing: 'border-box',
};

const GetDecisionInForm = () => {
    const { showError } = useError();

    const [criteriaData, setCriteriaData] = useState<AppCriteria>(initialAppCriteria);
    const [recommendation, setRecommendation] = useState<string>('');
    const [resultLoading, setResultLoading] = useState<boolean>(false);
    const [criteriaLoading, setCriteriaLoading] = useState<boolean>(false);

    useEffect(() => {
        setCriteriaLoading(true);

        getDecisionCriteria(GET_DECISION_IN_FORM_APP_ID, 'main')
            .then(response => setCriteriaData(response))
            .catch((err) => {
                showError({
                    code: err?.response?.status || 500,
                    message: 'API Error',
                    description: 'Failed to get decision criteria',
                    showRefresh: true,
                });
            })
            .finally(() => setCriteriaLoading(false));
    }, [showError]);

    const onCriteriaApply = useCallback(async () => {
        setResultLoading(true);

        try {
            const response = await rankApps(transformCriteriaData(criteriaData, {}),1,1,);
            const resultName = response?.results?.[0]?.data?.name;

            if (!resultName) {
                setRecommendation('');
                return;
            }

            setRecommendation(getFieldName(resultName));
        } catch (err: any) {
            showError({
                code: err?.response?.status || 500,
                message: 'API Error',
                description: 'Failed to get recommendation',
                showRefresh: true,
            });
        } finally {
            setResultLoading(false);
        }
    }, [criteriaData, showError]);

    const onSliderChange = useCallback((newValue: number, id: string) => {
        setCriteriaData(prev => {
            const fields = prev.fields;
            const idx = fields.findIndex(field => field.id === id);

            if (idx === -1) {
                return prev;
            }

            return {
                ...prev,
                fields: [
                    ...fields.slice(0, idx),
                    {
                        ...fields[idx],
                        importance: newValue,
                    },
                    ...fields.slice(idx + 1),
                ],
            };
        });
    }, []);

    return (
        <Box sx={{ width: '100%' }}>
            <AppCriteriaFields
                hideTitle
                criteriaData={criteriaData}
                criteriaConstraints={{}}
                id={GET_DECISION_IN_FORM_APP_ID + ''}
                onTradeoffValueChange={() => {
                }}
                setCriteriaConstraints={() => {
                }}
                setSelectedProductIds={() => {
                }}
                selectedProductIds={[]}
                onSliderBulkChange={() => {
                }}
                onCriteriaApply={onCriteriaApply}
                onSliderChange={onSliderChange}
                results={[]}
                pageInfo={pageInfo}
                onRerankMany={() => {
                }}
                onRerank={() => {
                }}
                fieldInitialValues={criteriaData.fields}
                onPageChange={() => {
                }}
                resultLoading={resultLoading}
                onResetTradeOffs={() => {
                }}
                criteriaLoading={criteriaLoading}
                onChangeCommitted={onCriteriaApply}
            />

            <Box sx={getDecisionInFormSx}>
                {resultLoading ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <CircularProgress size={18}/>
                        Thinking...
                    </span>
                ) : (
                    recommendation ? `Consider ${recommendation}.` : ''
                )}
            </Box>
        </Box>
    );
};

export default GetDecisionInForm;